<script setup>
import { computed } from 'vue';

import {
  formatAutonomyLevel,
  formatChannelType,
  playgroundContent,
} from '../../helpers/kraftchatAIPlaygroundConfig';

const props = defineProps({
  result: {
    type: Object,
    required: true,
  },
});

const effectiveSettings = computed(
  () => props.result?.effective_settings || {}
);

const enabledActionsLabel = computed(() => {
  const actions = effectiveSettings.value.allowed_actions_enabled || [];

  return actions.length
    ? actions.join(', ')
    : playgroundContent.noEnabledActionsLabel;
});

const safetySettingsLabel = computed(() => {
  const settings = effectiveSettings.value.safety_settings_enabled || [];

  return settings.length
    ? settings.join(', ')
    : playgroundContent.noEnabledSafetySettingsLabel;
});

const formatEnabled = value =>
  value ? playgroundContent.enabledLabel : playgroundContent.disabledLabel;
</script>

<template>
  <div class="space-y-4">
    <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ playgroundContent.settingsTitle }}
      </h4>

      <div class="mt-3 grid gap-3 text-sm text-n-slate-11 md:grid-cols-2">
        <p>
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.model }}
          </span>
          {{ effectiveSettings.model }}
        </p>

        <p>
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.temperature }}
          </span>
          {{ effectiveSettings.temperature }}
        </p>

        <p>
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.maxTokens }}
          </span>
          {{ effectiveSettings.max_tokens }}
        </p>

        <p>
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.autonomy }}
          </span>
          {{ formatAutonomyLevel(effectiveSettings.autonomy_level) }}
        </p>

        <p>
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.autoReply }}
          </span>
          {{ formatEnabled(effectiveSettings.auto_reply_enabled) }}
        </p>

        <p>
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.humanHandoff }}
          </span>
          {{ formatEnabled(effectiveSettings.human_handoff_enabled) }}
        </p>

        <p class="md:col-span-2">
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.tone }}
          </span>
          {{ effectiveSettings.tone }}
        </p>

        <p class="md:col-span-2">
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.instructions }}
          </span>
          {{ effectiveSettings.instructions }}
        </p>

        <p class="md:col-span-2">
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.greetingMessage }}
          </span>
          {{ effectiveSettings.greeting_message }}
        </p>

        <p class="md:col-span-2">
          <span class="font-medium text-n-slate-12">
            {{ playgroundContent.settingsLabels.handoffMessage }}
          </span>
          {{ effectiveSettings.handoff_message }}
        </p>
      </div>
    </div>

    <div class="grid gap-4 lg:grid-cols-2">
      <div class="space-y-3 rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ playgroundContent.detailsTitle }}
        </h4>

        <div class="space-y-2 text-sm text-n-slate-11">
          <p>
            <span class="font-medium text-n-slate-12">
              {{ playgroundContent.settingsLabels.agent }}
            </span>
            {{ result.agent?.name }}
          </p>

          <p>
            <span class="font-medium text-n-slate-12">
              {{ playgroundContent.settingsLabels.inbox }}
            </span>
            {{ result.agent?.inbox_name }}
          </p>

          <p>
            <span class="font-medium text-n-slate-12">
              {{ playgroundContent.settingsLabels.channel }}
            </span>
            {{ formatChannelType(result.agent?.channel_type) }}
          </p>

          <p>
            <span class="font-medium text-n-slate-12">
              {{ playgroundContent.settingsLabels.enabledActions }}
            </span>
            {{ enabledActionsLabel }}
          </p>

          <p>
            <span class="font-medium text-n-slate-12">
              {{ playgroundContent.settingsLabels.safetySettings }}
            </span>
            {{ safetySettingsLabel }}
          </p>
        </div>
      </div>

      <div class="space-y-3 rounded-xl border border-n-weak bg-n-solid-1 p-4">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ playgroundContent.actionsTitle }}
        </h4>

        <div v-if="result.suggested_actions?.length" class="space-y-2">
          <div
            v-for="action in result.suggested_actions"
            :key="action.key"
            class="rounded-lg border border-n-weak bg-n-background p-3"
          >
            <p class="text-sm font-medium text-n-slate-12">
              {{ action.label }}
            </p>

            <p class="mt-1 text-sm leading-5 text-n-slate-11">
              {{ action.reason }}
            </p>
          </div>
        </div>

        <p v-else class="text-sm text-n-slate-11">
          {{ playgroundContent.emptyActions }}
        </p>
      </div>
    </div>
  </div>
</template>
